import { PublishingMedium } from '../enums/PublishingMedium';
import {
  EOrganization,
  ETransfer,
  ERef,
  ETemplate,
  FirebaseTimestamp,
  Package,
  ModularSize
} from '.';
import { LineItem } from './invoices';
import { Layout } from './layout';
import { FilingType, OrderFilingType } from './filingType';
import { DisplayParams } from './notice';
import { EPayout } from './payout';
import { NewspaperOrderFeeSplitResults } from './feeSplit';
import { JobcaseSettings } from './jobcase';
import { TransferStatus } from './transfers';

export enum NewspaperOrderStatus {
  /**
   * Order has been archived and status has bubbled into all newspaper orders
   */
  ARCHIVED = 'archived',

  /**
   * Order has been cancelled & refunded.
   */
  CANCELLED = 'cancelled',

  /**
   * Newspaper order's last publishing date has elapsed and it is confirmed
   */
  COMPLETE = 'complete',

  /**
   * Soft deleted newspaper order, which allows us to keep transfer and other data
   */
  DELETED = 'deleted',

  /**
   * Order ad is in the process of being created
   */
  DRAFT = 'draft',

  /**
   * Order has been created and is awaiting review
   */
  AWAITING_REVIEW = 'awaiting review',

  /**
   * Order is being actively reviewed by the publisher
   */
  IN_REVIEW = 'in review',

  /**
   * Order has been confirmed by the publisher
   */
  CONFIRMED = 'confirmed'
}

export const PAGINATION_READY_STATUSES = [
  NewspaperOrderStatus.CONFIRMED,
  NewspaperOrderStatus.COMPLETE
];

export const PENDING_STATUSES = [
  NewspaperOrderStatus.AWAITING_REVIEW,
  NewspaperOrderStatus.IN_REVIEW
];

export type NewspaperOrderPricing = {
  lineItems: LineItem[];
  convenienceFeeInCents: number;

  /**
   * @property newspaperOrderFeeSplitResults tracks feeSplits as applied to this newspaper order
   * */
  newspaperOrderFeeSplitResults?: NewspaperOrderFeeSplitResults;

  subtotalInCents: number;

  /**
   * Amount to transfer to the publisher
   * This amount includes the taxes the newspaper charges, net of deduction of the application fee
   * Please use calculatePublisherAmountInCents() to calculate this amount
   */
  publisherAmountInCents: number;

  taxesInCents: number;
  taxPct: number;
  totalInCents: number;

  /**
   * Total discount applied to the newspaper order
   */
  totalDiscountInCents?: number;

  /**
   * Calculated application fee from organization-level settings
   * It is not added to the total, but is subtracted from the publisherAmountInCents
   * The application fee is independent from the convenience fee, though both are amounts that Column retains
   */
  applicationFeeInCents: number;
  /** Cumulative amount refunded (publisher + column portions) attributed to this newspaper order */
  totalRefundedInCents?: number;
  /** Portion of refunded amount reducing publisher exposure */
  publisherAmountRefundedInCents?: number;
  /** Portion of refunded amount attributed to Column-owned fees */
  convenienceFeeRefundedInCents?: number;
  /** Portion of refunded amount attributed to taxes */
  taxesRefundedInCents?: number;
  /** Portion of refunded amount attributed to the Column application fee */
  applicationFeeRefundedInCents?: number;
  /** Remaining publisher exposure after refunds.  This should be used as the amount to transfer to the publisher if populated. */
  netPublisherAmountInCents?: number;
  /** Sum of amounts for refunds that are staged (pending) but not yet completed or cancelled.  Used to easily guard against over-refunding */
  pendingRefundInCents?: number;
};

export enum NewspaperOrderLineItemStatus {
  /**
   * Line item is awaiting publication
   */
  PENDING = 'pending',

  /**
   * Line item has been published in the newspaper
   */
  PUBLISHED = 'published',

  /**
   * Line item has been cancelled
   */
  CANCELLED = 'cancelled'
}

export type NewspaperOrderLineItem = {
  amount: number;
  date: string;
  description: string;
  status: NewspaperOrderLineItemStatus;
};

type ColorOptions = {
  isGrayscale: boolean;
  backgroundColor: string;
  borderColor: string;
};

export type ColorSettingVisibility = {
  showColorToggle: boolean;
  showBackgroundColor: boolean;
  showBorderColor: boolean;
  showTextColor: boolean;
};

export type JobcaseEnablement = {
  upsellDescription: string | undefined;
  showJobcase: boolean;
};

export type NewspaperOrder = {
  /**
   * Subcollections:
   *  NewspaperOrderTransaction
   */

  /** The edit version of the order to which the newspaperOrder belongs */
  orderVersion: number;
  newspaper: ERef<EOrganization>;
  publishingMedium: PublishingMedium;
  package?: ERef<Package> | null;
  adTemplate?: ERef<ETemplate>;
  /** A fixed size to apply to the order when it is rendered, configured as a set of sizes maintained by the publisher */
  modularSize?: ERef<ModularSize>;
  filingType: ERef<OrderFilingType>;
  layout: Layout;
  publishingDates: string[];
  pricing?: NewspaperOrderPricing;
  transfer?: ERef<ETransfer>;
  transferStatus?: TransferStatus;
  payout?: ERef<EPayout>;
  status: NewspaperOrderStatus;
  pdfStoragePath?: string;
  displayParams?: DisplayParams;
  confirmedAt?: FirebaseTimestamp;

  /**
   * User-selected color settings in this newspaperOrder
   */
  colorOptions?: ColorOptions;

  /**
   * The color support within this newspaperOrder, defined largely if not completely by the rate
   * attached to this newspaperOrder. Consolidating settings here allows us to not reload rate data
   */
  colorEnablement: ColorSettingVisibility;

  /**
   * User-selected jobcase settings in this newspaperOrder
   */
  jobcaseSettings?: JobcaseSettings;

  /**
   * Jobcase support within this newsapaperOrder, defined by the rate attached to this newspaperOrder.
   * Consolidating settings here allows us to not reload rate data through placement flow.
   */
  jobcaseEnablement: JobcaseEnablement;

  /**
   * Order number in the publisher's system (e.g., AdBase order ID).
   * This is populated after a successful sync to the publisher's ad management system.
   * Mirrors the customId field on notices for consistency.
   */
  publisherId?: string | null;

  /**
   * @deprecated
   * This prop is being deleted. Use newspaperOrder.filingType instead!
   */
  oldFilingType: FilingType;
};

export function validateCompleteNewspaperOrder(
  newspaperOrder: Partial<NewspaperOrder>
): newspaperOrder is NewspaperOrder {
  return (
    newspaperOrder !== null &&
    typeof newspaperOrder === 'object' &&
    newspaperOrder.newspaper !== undefined &&
    newspaperOrder.filingType !== undefined &&
    newspaperOrder.layout !== undefined &&
    Array.isArray(newspaperOrder.publishingDates) &&
    newspaperOrder.publishingDates.length > 0 &&
    newspaperOrder.publishingDates.every(date => typeof date === 'string') &&
    newspaperOrder.status !== undefined &&
    Object.values(NewspaperOrderStatus).includes(newspaperOrder.status)
  );
}

export type NewspaperOrderRefundItem = {
  newspaperOrderId: string;
  amountInCents: number;
};

export type NewspaperOrderSearchRequest = {
  newspaperIds: string[];

  // YYYY-MM-DD format
  publishingDate?: string;

  statuses?: NewspaperOrderStatus[];
};
